import { 
  StoryScene, 
  StoryCharacter, 
  StoryDialogue, 
  FillInBlankChallenge, 
  MultipleChoiceChallenge, 
  DialogueSimulationChallenge, 
  TranslationChallenge 
} from '../types/Story';
import { getDailyContent, getTodayDateString } from '../utils/dailyContent';

// Characters
export const storyCharacters: StoryCharacter[] = [
  {
    id: 'yuki',
    name: 'Yuki',
    nameJp: 'ゆき',
    nameRomaji: 'Yuki',
    description: 'A friendly Japanese exchange student',
    avatar: '👩‍🎓'
  },
  {
    id: 'taro',
    name: 'Taro',
    nameJp: 'たろう',
    nameRomaji: 'Tarou',
    description: 'A helpful local guide',
    avatar: '👨‍💼'
  },
  {
    id: 'sakura',
    name: 'Sakura',
    nameJp: 'さくら',
    nameRomaji: 'Sakura',
    description: 'A café owner',
    avatar: '👩‍🍳'
  },
  {
    id: 'kenji',
    name: 'Kenji',
    nameJp: 'けんじ',
    nameRomaji: 'Kenji',
    description: 'A university student',
    avatar: '👨‍🎓'
  }
];

// Story Scenes
export const storyScenes: StoryScene[] = [
  {
    id: 'airport-arrival',
    title: 'Airport Arrival',
    titleJp: '空港到着',
    description: 'You arrive at Tokyo Airport and meet Yuki, who will help you get settled.',
    background: '🏢',
    characters: [storyCharacters[0]], // Yuki
    dialogue: [
      {
        id: 'd1',
        characterId: 'yuki',
        text: 'Welcome to Japan! I\'m Yuki. Nice to meet you.',
        textJp: '日本へようこそ！私はゆきです。よろしくお願いします。',
        textRomaji: 'Nihon e youkoso! Watashi wa Yuki desu. Yoroshiku onegaishimasu.',
        translation: 'Welcome to Japan! I\'m Yuki. Nice to meet you.'
      },
      {
        id: 'd2',
        characterId: 'yuki',
        text: 'How was your flight?',
        textJp: 'フライトはどうでしたか？',
        textRomaji: 'Furaito wa dou deshita ka?',
        translation: 'How was your flight?'
      }
    ],
    challenges: [
      {
        id: 'c1',
        type: 'fill-in-blank',
        question: 'Complete the greeting response',
        sentence: 'Nice to meet you too. My name is ___ .',
        sentenceJp: 'こちらこそよろしくお願いします。私の名前は___です。',
        sentenceRomaji: 'Kochira koso yoroshiku onegaishimasu. Watashi no namae wa ___ desu.',
        blankPosition: 3,
        correctAnswer: 'John',
        correctAnswerJp: 'ジョン',
        explanation: 'When introducing yourself in Japanese, you say your name followed by です (desu).',
        difficulty: 'beginner',
        points: 10,
        hint: 'Think of a common English name'
      } as FillInBlankChallenge,
      {
        id: 'c2',
        type: 'multiple-choice',
        question: 'How do you respond to "How was your flight?" in Japanese?',
        questionJp: '「フライトはどうでしたか？」にどう答えますか？',
        options: ['It was good', 'It was bad', 'It was okay', 'I don\'t know'],
        optionsJp: ['良かったです', '悪かったです', 'まあまあでした', '分かりません'],
        correctAnswer: 'It was good',
        correctAnswerJp: '良かったです',
        explanation: '良かったです (yokatta desu) means "it was good" and is a polite way to respond.',
        difficulty: 'beginner',
        points: 15
      } as MultipleChoiceChallenge
    ]
  },
  {
    id: 'cafe-conversation',
    title: 'Café Conversation',
    titleJp: 'カフェでの会話',
    description: 'You visit a local café and practice ordering in Japanese.',
    background: '☕',
    characters: [storyCharacters[2]], // Sakura
    dialogue: [
      {
        id: 'd3',
        characterId: 'sakura',
        text: 'Welcome! What would you like to order?',
        textJp: 'いらっしゃいませ！何をお注文されますか？',
        textRomaji: 'Irasshaimase! Nani o gochuumon saremasu ka?',
        translation: 'Welcome! What would you like to order?'
      }
    ],
    challenges: [
      {
        id: 'c3',
        type: 'dialogue-simulation',
        scenario: 'You want to order a coffee. Choose the most appropriate response.',
        scenarioJp: 'コーヒーを注文したいです。最も適切な返答を選んでください。',
        userChoices: [
          'I want coffee',
          'Coffee please',
          'Can I have a coffee?',
          'Give me coffee'
        ],
        userChoicesJp: [
          'コーヒーが欲しい',
          'コーヒーをお願いします',
          'コーヒーをいただけますか？',
          'コーヒーをください'
        ],
        correctChoiceIndex: 2,
        responses: [
          'That\'s too direct',
          'Good, but could be more polite',
          'Perfect! Very polite',
          'Too casual for a café'
        ],
        responsesJp: [
          '少し直接すぎます',
          '良いですが、もっと丁寧にできます',
          '完璧です！とても丁寧です',
          'カフェには少しカジュアルすぎます'
        ],
        correctAnswer: 'Can I have a coffee?',
        correctAnswerJp: 'コーヒーをいただけますか？',
        explanation: 'In Japanese culture, being polite is very important, especially when ordering at restaurants.',
        difficulty: 'intermediate',
        points: 20
      } as DialogueSimulationChallenge,
      {
        id: 'c4',
        type: 'translation',
        question: 'Translate "Thank you very much" to Japanese',
        sourceText: 'Thank you very much',
        sourceTextJp: 'ありがとうございます',
        targetLanguage: 'jp',
        acceptableAnswers: ['ありがとうございます', 'どうもありがとうございます'],
        correctAnswer: 'ありがとうございます',
        explanation: 'ありがとうございます (arigatou gozaimasu) is the polite form of "thank you".',
        difficulty: 'beginner',
        points: 10
      } as TranslationChallenge
    ]
  },
  {
    id: 'university-life',
    title: 'University Life',
    titleJp: '大学生活',
    description: 'You attend your first Japanese class and meet fellow students.',
    background: '🏫',
    characters: [storyCharacters[3]], // Kenji
    dialogue: [
      {
        id: 'd4',
        characterId: 'kenji',
        text: 'Hi! Are you new to this class?',
        textJp: 'こんにちは！このクラスは初めてですか？',
        textRomaji: 'Konnichiwa! Kono kurasu wa hajimete desu ka?',
        translation: 'Hi! Are you new to this class?'
      },
      {
        id: 'd5',
        characterId: 'kenji',
        text: 'I can help you with Japanese if you need it.',
        textJp: '日本語で困ったことがあれば、お手伝いできます。',
        textRomaji: 'Nihongo de komatta koto ga areba, otetsudai dekimasu.',
        translation: 'I can help you with Japanese if you need it.'
      }
    ],
    challenges: [
      {
        id: 'c5',
        type: 'fill-in-blank',
        question: 'Complete the response to "Are you new?"',
        sentence: 'Yes, this is my ___ time here.',
        sentenceJp: 'はい、ここは___回目です。',
        sentenceRomaji: 'Hai, koko wa ___ kai me desu.',
        blankPosition: 2,
        correctAnswer: 'first',
        correctAnswerJp: '初',
        explanation: '初回 (shokai) means "first time" in Japanese.',
        difficulty: 'intermediate',
        points: 15,
        hint: 'Think of ordinal numbers'
      } as FillInBlankChallenge,
      {
        id: 'c6',
        type: 'multiple-choice',
        question: 'What does お手伝い (otetsudai) mean?',
        questionJp: '「お手伝い」はどういう意味ですか？',
        options: ['Help', 'Study', 'Teach', 'Learn'],
        correctAnswer: 'Help',
        correctAnswerJp: '手伝い',
        explanation: 'お手伝い (otetsudai) means "help" or "assistance" in Japanese.',
        difficulty: 'intermediate',
        points: 20
      } as MultipleChoiceChallenge
    ]
  },
  {
    id: 'shopping-adventure',
    title: 'Shopping Adventure',
    titleJp: '買い物の冒険',
    description: 'You go shopping and need to ask for help finding items.',
    background: '🛍️',
    characters: [storyCharacters[1]], // Taro
    dialogue: [
      {
        id: 'd6',
        characterId: 'taro',
        text: 'Can I help you find something?',
        textJp: '何かお探しでしょうか？',
        textRomaji: 'Nanika osagashi deshou ka?',
        translation: 'Can I help you find something?'
      }
    ],
    challenges: [
      {
        id: 'c7',
        type: 'dialogue-simulation',
        scenario: 'You\'re looking for a bookstore. How do you ask?',
        scenarioJp: '本屋を探しています。どう聞きますか？',
        userChoices: [
          'Where is the bookstore?',
          'I need a bookstore',
          'Can you tell me where the bookstore is?',
          'Bookstore please'
        ],
        userChoicesJp: [
          '本屋はどこですか？',
          '本屋が必要です',
          '本屋はどこにあるか教えていただけますか？',
          '本屋をお願いします'
        ],
        correctChoiceIndex: 2,
        responses: [
          'Direct but acceptable',
          'Too direct',
          'Very polite and clear',
          'Not clear enough'
        ],
        responsesJp: [
          '直接的ですが許容できます',
          '少し直接的すぎます',
          'とても丁寧で明確です',
          '十分に明確ではありません'
        ],
        correctAnswer: 'Can you tell me where the bookstore is?',
        correctAnswerJp: '本屋はどこにあるか教えていただけますか？',
        explanation: 'Using 教えていただけますか (oshiete itadakemasu ka) is a very polite way to ask for information.',
        difficulty: 'advanced',
        points: 25
      } as DialogueSimulationChallenge,
      {
        id: 'c8',
        type: 'translation',
        question: 'Translate "I\'m looking for a gift" to Japanese',
        sourceText: 'I\'m looking for a gift',
        sourceTextJp: 'プレゼントを探しています',
        targetLanguage: 'jp',
        acceptableAnswers: ['プレゼントを探しています', '贈り物を探しています'],
        correctAnswer: 'プレゼントを探しています',
        explanation: 'プレゼントを探しています (purezento o sagashite imasu) means "I\'m looking for a gift".',
        difficulty: 'intermediate',
        points: 15
      } as TranslationChallenge
    ]
  }
];

// Helper function to get a scene by ID
export const getSceneById = (sceneId: string): StoryScene | undefined => {
  return storyScenes.find(scene => scene.id === sceneId);
};

// Helper function to get a character by ID
export const getCharacterById = (characterId: string): StoryCharacter | undefined => {
  return storyCharacters.find(character => character.id === characterId);
};

// Helper function to get daily scene (deterministic based on date)
export const getDailyScene = (): StoryScene => {
  return getDailyContent(storyScenes);
};

// Story data for "Go for it, Yuta!" (がんばれ、ユウタ！)
// Each day is a segment with 5 new words, simple dialogue, and 5 fill-in-the-blank lines.

export interface StoryTile {
  jp: string;
  romaji: string;
}

export interface StoryLine {
  id: string;
  speaker: 'yuta' | 'friend' | 'teacher' | 'narrator' | 'obaasan' | 'ojiisan' | 'momotaro' | 'inu' | 'saru' | 'kiji' | 'oni' | 'villager';
  avatar: string;
  type: 'normal' | 'fill-blank';
  jp: string; // Japanese (kanji/kana, use ___ for blank)
  hiragana: string[]; // Array of hiragana for each kanji chunk (for popover)
  romaji: string;
  en: string;
  audioUrl?: string;
  // For fill-blank
  tiles?: StoryTile[]; // Draggable options (jp + romaji)
  answer?: StoryTile; // Correct answer (jp + romaji)
}

export interface StoryDay {
  day: number;
  title: string;
  titleJp: string;
  newWords: StoryTile[];
  lines: StoryLine[];
}

export const storyDays: StoryDay[] = [
  // Day 1: Introduction to Momotaro
  {
    day: 1,
    title: "The Peach Boy",
    titleJp: "桃太郎",
    newWords: [
      { jp: 'もも', romaji: 'momo' },
      { jp: 'おじいさん', romaji: 'ojiisan' },
      { jp: 'おばあさん', romaji: 'obaasan' },
      { jp: 'うまれました', romaji: 'umaremashita' },
    ],
    lines: [
      {
        id: '1-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'むかしむかし、おじいさんとおばあさんが住んでいました。',
        hiragana: ['むかしむかし','おじいさん','と','おばあさん','が','すんでいました'],
        romaji: 'Mukashi mukashi, ojiisan to obaasan ga sunde imashita.',
        en: "Long, long ago, there lived an old man and an old woman.",
      },
      {
        id: '1-2',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: 'おじいさん、___を洗いに行きましょう。',
        hiragana: ['おじいさん','もも','を','あらい','に','いきましょう'],
        romaji: 'Ojiisan, ___ o arai ni ikimashou.',
        en: "Grandfather, let's go wash the peaches.",
        tiles: [
          { jp: 'もも', romaji: 'momo' },
          { jp: 'りんご', romaji: 'ringo' },
          { jp: 'みかん', romaji: 'mikan' },
        ],
        answer: { jp: 'もも', romaji: 'momo' },
      },
      {
        id: '1-3',
        speaker: 'ojiisan',
        avatar: '👴',
        type: 'normal',
        jp: 'はい、行きましょう。',
        hiragana: ['はい','いきましょう'],
        romaji: 'Hai, ikimashou.',
        en: "Yes, let's go.",
      },
      {
        id: '1-4',
        speaker: 'narrator',
        avatar: '📖',
        type: 'fill-blank',
        jp: '川で洗っていると、大きな___が流れてきました。',
        hiragana: ['かわ','で','あらって','いる','と','おおきな','もも','が','ながれて','きました'],
        romaji: 'Kawa de aratte iru to, ookina ___ ga nagarete kimashita.',
        en: "While washing in the river, a big peach came floating down.",
        tiles: [
          { jp: 'もも', romaji: 'momo' },
          { jp: 'いぬ', romaji: 'inu' },
          { jp: 'ねこ', romaji: 'neko' },
        ],
        answer: { jp: 'もも', romaji: 'momo' },
      },
      {
        id: '1-5',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: 'おじいさん、この___を持って帰りましょう。',
        hiragana: ['おじいさん','この','もも','を','もって','かえりましょう'],
        romaji: 'Ojiisan, kono ___ o motte kaerimashou.',
        en: "Grandfather, let's take this peach home.",
        tiles: [
          { jp: 'もも', romaji: 'momo' },
          { jp: 'りんご', romaji: 'ringo' },
          { jp: 'みかん', romaji: 'mikan' },
        ],
        answer: { jp: 'もも', romaji: 'momo' },
      },
    ]
  },
  // Day 2: Momotaro is born
  {
    day: 2,
    title: "A Boy from the Peach",
    titleJp: "桃から生まれた男の子",
    newWords: [
      { jp: 'なか', romaji: 'naka' },
      { jp: 'こども', romaji: 'kodomo' },
      { jp: 'なまえ', romaji: 'namae' },
      { jp: 'ももたろう', romaji: 'momotarou' },
    ],
    lines: [
      {
        id: '2-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: '家に帰って、ももを切ると、中から男の子が出てきました。',
        hiragana: ['いえ','に','かえって','もも','を','きる','と','なか','から','おとこのこ','が','でて','きました'],
        romaji: 'Ie ni kaette, momo o kiru to, naka kara otoko no ko ga dete kimashita.',
        en: "When they got home and cut the peach, a boy came out from inside.",
      },
      {
        id: '2-2',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: 'まあ、___が生まれました！',
        hiragana: ['まあ','こども','が','うまれました'],
        romaji: 'Maa, ___ ga umaremashita!',
        en: "Oh my, a child was born!",
        tiles: [
          { jp: 'こども', romaji: 'kodomo' },
          { jp: 'いぬ', romaji: 'inu' },
          { jp: 'ねこ', romaji: 'neko' },
        ],
        answer: { jp: 'こども', romaji: 'kodomo' },
      },
      {
        id: '2-3',
        speaker: 'ojiisan',
        avatar: '👴',
        type: 'fill-blank',
        jp: 'この子の___は何にしましょうか？',
        hiragana: ['この','こ','の','なまえ','は','なに','に','しましょう','か'],
        romaji: 'Kono ko no ___ wa nani ni shimashou ka?',
        en: "What should we name this child?",
        tiles: [
          { jp: 'なまえ', romaji: 'namae' },
          { jp: 'かばん', romaji: 'kaban' },
          { jp: 'ほん', romaji: 'hon' },
        ],
        answer: { jp: 'なまえ', romaji: 'namae' },
      },
      {
        id: '2-4',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: 'ももから生まれたから、___にしましょう。',
        hiragana: ['もも','から','うまれた','から','ももたろう','に','しましょう'],
        romaji: 'Momo kara umareta kara, ___ ni shimashou.',
        en: "Since he was born from a peach, let's call him Momotaro.",
        tiles: [
          { jp: 'ももたろう', romaji: 'momotarou' },
          { jp: 'たなか', romaji: 'tanaka' },
          { jp: 'やまだ', romaji: 'yamada' },
        ],
        answer: { jp: 'ももたろう', romaji: 'momotarou' },
      },
      {
        id: '2-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'ももたろうです。よろしくお願いします。',
        hiragana: ['ももたろう','です','よろしく','おねがい','します'],
        romaji: 'Momotarou desu. Yoroshiku onegaishimasu.',
        en: "I'm Momotaro. Nice to meet you.",
      },
    ]
  },
  // Day 3: Momotaro grows up
  {
    day: 3,
    title: "Growing Up Strong",
    titleJp: "強く育つ",
    newWords: [
      { jp: 'おおきく', romaji: 'ookiku' },
      { jp: 'つよく', romaji: 'tsuyoku' },
      { jp: 'べんきょう', romaji: 'benkyou' },
      { jp: 'がんばりました', romaji: 'ganbarimashita' },
    ],
    lines: [
      {
        id: '3-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'ももたろうは毎日おじいさんとおばあさんと一緒に暮らしました。',
        hiragana: ['ももたろう','は','まいにち','おじいさん','と','おばあさん','と','いっしょ','に','くらしました'],
        romaji: 'Momotarou wa mainichi ojiisan to obaasan to issho ni kurashimashita.',
        en: "Momotaro lived with his grandparents every day.",
      },
      {
        id: '3-2',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: 'ももたろう、___なりましたね。',
        hiragana: ['ももたろう','おおきく','なりました','ね'],
        romaji: 'Momotarou, ___ narimashita ne.',
        en: "Momotaro, you've grown big.",
        tiles: [
          { jp: 'おおきく', romaji: 'ookiku' },
          { jp: 'ちいさく', romaji: 'chiisaku' },
          { jp: 'よわく', romaji: 'yowaku' },
        ],
        answer: { jp: 'おおきく', romaji: 'ookiku' },
      },
      {
        id: '3-3',
        speaker: 'ojiisan',
        avatar: '👴',
        type: 'fill-blank',
        jp: 'とても___な子になりました。',
        hiragana: ['とても','つよく','な','こ','に','なりました'],
        romaji: 'Totemo ___ na ko ni narimashita.',
        en: "He has become a very strong child.",
        tiles: [
          { jp: 'つよく', romaji: 'tsuyoku' },
          { jp: 'よわく', romaji: 'yowaku' },
          { jp: 'おそく', romaji: 'osoku' },
        ],
        answer: { jp: 'つよく', romaji: 'tsuyoku' },
      },
      {
        id: '3-4',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: '毎日___しています。',
        hiragana: ['まいにち','べんきょう','して','います'],
        romaji: 'Mainichi ___ shite imasu.',
        en: "I study every day.",
        tiles: [
          { jp: 'べんきょう', romaji: 'benkyou' },
          { jp: 'あそび', romaji: 'asobi' },
          { jp: 'ねむり', romaji: 'nemuri' },
        ],
        answer: { jp: 'べんきょう', romaji: 'benkyou' },
      },
      {
        id: '3-5',
        speaker: 'narrator',
        avatar: '📖',
        type: 'fill-blank',
        jp: 'ももたろうは一生懸命___。',
        hiragana: ['ももたろう','は','いっしょうけんめい','がんばりました'],
        romaji: 'Momotarou wa isshoukenmei ___.',
        en: "Momotaro worked very hard.",
        tiles: [
          { jp: 'がんばりました', romaji: 'ganbarimashita' },
          { jp: 'あそびました', romaji: 'asobimashita' },
          { jp: 'ねました', romaji: 'nemashita' },
        ],
        answer: { jp: 'がんばりました', romaji: 'ganbarimashita' },
      },
    ]
  },
  // Day 4: The ogres appear
  {
    day: 4,
    title: "The Ogre Problem",
    titleJp: "鬼の問題",
    newWords: [
      { jp: 'おに', romaji: 'oni' },
      { jp: 'しま', romaji: 'shima' },
      { jp: 'わるい', romaji: 'warui' },
      { jp: 'たすけて', romaji: 'tasukete' },
    ],
    lines: [
      {
        id: '4-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'ある日、村の人たちが困っていました。',
        hiragana: ['ある','ひ','むら','の','ひとたち','が','こまって','いました'],
        romaji: 'Aru hi, mura no hitotachi ga komatte imashita.',
        en: "One day, the villagers were in trouble.",
      },
      {
        id: '4-2',
        speaker: 'villager',
        avatar: '👨',
        type: 'fill-blank',
        jp: '___が島に住んでいます。',
        hiragana: ['おに','が','しま','に','すんで','います'],
        romaji: '___ ga shima ni sunde imasu.',
        en: "Ogres live on the island.",
        tiles: [
          { jp: 'おに', romaji: 'oni' },
          { jp: 'いぬ', romaji: 'inu' },
          { jp: 'ねこ', romaji: 'neko' },
        ],
        answer: { jp: 'おに', romaji: 'oni' },
      },
      {
        id: '4-3',
        speaker: 'villager',
        avatar: '👨',
        type: 'fill-blank',
        jp: 'その___はとても悪いです。',
        hiragana: ['その','おに','は','とても','わるい','です'],
        romaji: 'Sono ___ wa totemo warui desu.',
        en: "Those ogres are very bad.",
        tiles: [
          { jp: 'おに', romaji: 'oni' },
          { jp: 'いぬ', romaji: 'inu' },
          { jp: 'ねこ', romaji: 'neko' },
        ],
        answer: { jp: 'おに', romaji: 'oni' },
      },
      {
        id: '4-4',
        speaker: 'villager',
        avatar: '👨',
        type: 'fill-blank',
        jp: '私たちを___ください。',
        hiragana: ['わたしたち','を','たすけて','ください'],
        romaji: 'Watashitachi o ___ kudasai.',
        en: "Please help us.",
        tiles: [
          { jp: 'たすけて', romaji: 'tasukete' },
          { jp: 'おしえて', romaji: 'oshiete' },
          { jp: 'みて', romaji: 'mite' },
        ],
        answer: { jp: 'たすけて', romaji: 'tasukete' },
      },
      {
        id: '4-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: '私が鬼を退治します！',
        hiragana: ['わたし','が','おに','を','たいじ','します'],
        romaji: 'Watashi ga oni o taiji shimasu!',
        en: "I will defeat the ogres!",
      },
    ]
  },
  // Day 5: Momotaro prepares for the journey
  {
    day: 5,
    title: "Preparing for the Journey",
    titleJp: "旅の準備",
    newWords: [
      { jp: 'たび', romaji: 'tabi' },
      { jp: 'じゅんび', romaji: 'junbi' },
      { jp: 'きびだんご', romaji: 'kibidango' },
      { jp: 'つくって', romaji: 'tsukutte' },
    ],
    lines: [
      {
        id: '5-1',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'normal',
        jp: 'ももたろう、旅の準備をしましょう。',
        hiragana: ['ももたろう','たび','の','じゅんび','を','しましょう'],
        romaji: 'Momotarou, tabi no junbi o shimashou.',
        en: "Momotaro, let's prepare for the journey.",
      },
      {
        id: '5-2',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: '___を作ってあげます。',
        hiragana: ['きびだんご','を','つくって','あげます'],
        romaji: '___ o tsukutte agemasu.',
        en: "I'll make millet dumplings for you.",
        tiles: [
          { jp: 'きびだんご', romaji: 'kibidango' },
          { jp: 'おにぎり', romaji: 'onigiri' },
          { jp: 'サンドイッチ', romaji: 'sandoicchi' },
        ],
        answer: { jp: 'きびだんご', romaji: 'kibidango' },
      },
      {
        id: '5-3',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'おばあさん、ありがとうございます。___を大切にします。',
        hiragana: ['おばあさん','ありがとう','ございます','きびだんご','を','たいせつ','に','します'],
        romaji: 'Obaasan, arigatou gozaimasu. ___ o taisetsu ni shimasu.',
        en: "Grandmother, thank you. I'll treasure the millet dumplings.",
        tiles: [
          { jp: 'きびだんご', romaji: 'kibidango' },
          { jp: 'おにぎり', romaji: 'onigiri' },
          { jp: 'サンドイッチ', romaji: 'sandoicchi' },
        ],
        answer: { jp: 'きびだんご', romaji: 'kibidango' },
      },
      {
        id: '5-4',
        speaker: 'ojiisan',
        avatar: '👴',
        type: 'fill-blank',
        jp: '気をつけて行ってらっしゃい。',
        hiragana: ['き','を','つけて','いって','らっしゃい'],
        romaji: 'Ki o tsukete itte rasshai.',
        en: "Take care and go safely.",
      },
      {
        id: '5-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'はい、行ってきます！',
        hiragana: ['はい','いって','きます'],
        romaji: 'Hai, itte kimasu!',
        en: "Yes, I'm going!",
      },
    ]
  },
  // Day 6: Meeting the dog
  {
    day: 6,
    title: "Meeting the Dog",
    titleJp: "犬との出会い",
    newWords: [
      { jp: 'いぬ', romaji: 'inu' },
      { jp: 'なかま', romaji: 'nakama' },
      { jp: 'いっしょに', romaji: 'issho ni' },
      { jp: 'いきましょう', romaji: 'ikimashou' },
    ],
    lines: [
      {
        id: '6-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'ももたろうが道を歩いていると、犬が来ました。',
        hiragana: ['ももたろう','が','みち','を','あるいて','いる','と','いぬ','が','きました'],
        romaji: 'Momotarou ga michi o aruite iru to, inu ga kimashita.',
        en: "As Momotaro was walking on the road, a dog came.",
      },
      {
        id: '6-2',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___をください。',
        hiragana: ['ワンワン','きびだんご','を','ください'],
        romaji: 'Wanwan! ___ o kudasai.',
        en: "Woof woof! Please give me a millet dumpling.",
        tiles: [
          { jp: 'きびだんご', romaji: 'kibidango' },
          { jp: 'おにぎり', romaji: 'onigiri' },
          { jp: 'サンドイッチ', romaji: 'sandoicchi' },
        ],
        answer: { jp: 'きびだんご', romaji: 'kibidango' },
      },
      {
        id: '6-3',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'はい、どうぞ。___になりませんか？',
        hiragana: ['はい','どうぞ','なかま','に','なりません','か'],
        romaji: 'Hai, douzo. ___ ni narimasen ka?',
        en: "Here you go. Won't you become my companion?",
        tiles: [
          { jp: 'なかま', romaji: 'nakama' },
          { jp: 'ともだち', romaji: 'tomodachi' },
          { jp: 'せんせい', romaji: 'sensei' },
        ],
        answer: { jp: 'なかま', romaji: 'nakama' },
      },
      {
        id: '6-4',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'はい！___に行きましょう。',
        hiragana: ['はい','いっしょに','いきましょう'],
        romaji: 'Hai! ___ ni ikimashou.',
        en: "Yes! Let's go together.",
        tiles: [
          { jp: 'いっしょに', romaji: 'issho ni' },
          { jp: 'ひとりで', romaji: 'hitori de' },
          { jp: 'あとで', romaji: 'ato de' },
        ],
        answer: { jp: 'いっしょに', romaji: 'issho ni' },
      },
      {
        id: '6-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'よろしく、犬さん。',
        hiragana: ['よろしく','いぬ','さん'],
        romaji: 'Yoroshiku, inu-san.',
        en: "Nice to meet you, Mr. Dog.",
      },
    ]
  },
  // Day 7: Meeting the monkey
  {
    day: 7,
    title: "Meeting the Monkey",
    titleJp: "猿との出会い",
    newWords: [
      { jp: 'さる', romaji: 'saru' },
      { jp: 'かしこい', romaji: 'kashikoi' },
      { jp: 'てつだい', romaji: 'tetsudai' },
      { jp: 'よろしく', romaji: 'yoroshiku' },
    ],
    lines: [
      {
        id: '7-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: '少し歩くと、猿が木の上にいました。',
        hiragana: ['すこし','あるく','と','さる','が','き','の','うえ','に','いました'],
        romaji: 'Sukoshi aruku to, saru ga ki no ue ni imashita.',
        en: "After walking a bit, there was a monkey in the tree.",
      },
      {
        id: '7-2',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___をください。',
        hiragana: ['きーきー','きびだんご','を','ください'],
        romaji: 'Kiiki! ___ o kudasai.',
        en: "Kee-kee! Please give me a millet dumpling.",
        tiles: [
          { jp: 'きびだんご', romaji: 'kibidango' },
          { jp: 'おにぎり', romaji: 'onigiri' },
          { jp: 'サンドイッチ', romaji: 'sandoicchi' },
        ],
        answer: { jp: 'きびだんご', romaji: 'kibidango' },
      },
      {
        id: '7-3',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'はい、どうぞ。___な猿さんですね。',
        hiragana: ['はい','どうぞ','かしこい','な','さる','さん','ですね'],
        romaji: 'Hai, douzo. ___ na saru-san desu ne.',
        en: "Here you go. You're a smart monkey.",
        tiles: [
          { jp: 'かしこい', romaji: 'kashikoi' },
          { jp: 'つよい', romaji: 'tsuyoi' },
          { jp: 'おおきい', romaji: 'ookii' },
        ],
        answer: { jp: 'かしこい', romaji: 'kashikoi' },
      },
      {
        id: '7-4',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: '鬼退治の___をします。',
        hiragana: ['おに','たいじ','の','てつだい','を','します'],
        romaji: 'Oni taiji no ___ o shimasu.',
        en: "I'll help with defeating the ogres.",
        tiles: [
          { jp: 'てつだい', romaji: 'tetsudai' },
          { jp: 'べんきょう', romaji: 'benkyou' },
          { jp: 'あそび', romaji: 'asobi' },
        ],
        answer: { jp: 'てつだい', romaji: 'tetsudai' },
      },
      {
        id: '7-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: '___、猿さん。',
        hiragana: ['よろしく','さる','さん'],
        romaji: '___, saru-san.',
        en: "Nice to meet you, Mr. Monkey.",
        tiles: [
          { jp: 'よろしく', romaji: 'yoroshiku' },
          { jp: 'さようなら', romaji: 'sayounara' },
          { jp: 'ありがとう', romaji: 'arigatou' },
        ],
        answer: { jp: 'よろしく', romaji: 'yoroshiku' },
      },
    ]
  },
  // Day 8: Meeting the pheasant
  {
    day: 8,
    title: "Meeting the Pheasant",
    titleJp: "雉との出会い",
    newWords: [
      { jp: 'きじ', romaji: 'kiji' },
      { jp: 'そら', romaji: 'sora' },
      { jp: 'とべる', romaji: 'toberu' },
      { jp: 'みえる', romaji: 'mieru' },
    ],
    lines: [
      {
        id: '8-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'さらに歩くと、雉が空を飛んでいました。',
        hiragana: ['さらに','あるく','と','きじ','が','そら','を','とんで','いました'],
        romaji: 'Sarani aruku to, kiji ga sora o tonde imashita.',
        en: "Walking further, a pheasant was flying in the sky.",
      },
      {
        id: '8-2',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'fill-blank',
        jp: 'ケンケン！___をください。',
        hiragana: ['ケンケン','きびだんご','を','ください'],
        romaji: 'Kenken! ___ o kudasai.',
        en: "Ken-ken! Please give me a millet dumpling.",
        tiles: [
          { jp: 'きびだんご', romaji: 'kibidango' },
          { jp: 'おにぎり', romaji: 'onigiri' },
          { jp: 'サンドイッチ', romaji: 'sandoicchi' },
        ],
        answer: { jp: 'きびだんご', romaji: 'kibidango' },
      },
      {
        id: '8-3',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'はい、どうぞ。___を飛べるんですね。',
        hiragana: ['はい','どうぞ','そら','を','とべる','ん','ですね'],
        romaji: 'Hai, douzo. ___ o toberun desu ne.',
        en: "Here you go. You can fly in the sky.",
        tiles: [
          { jp: 'そら', romaji: 'sora' },
          { jp: 'うみ', romaji: 'umi' },
          { jp: 'やま', romaji: 'yama' },
        ],
        answer: { jp: 'そら', romaji: 'sora' },
      },
      {
        id: '8-4',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'fill-blank',
        jp: 'はい、___から遠くまで見えます。',
        hiragana: ['はい','そら','から','とおく','まで','みえます'],
        romaji: 'Hai, ___ kara tooku made miemasu.',
        en: "Yes, I can see far from the sky.",
        tiles: [
          { jp: 'そら', romaji: 'sora' },
          { jp: 'うみ', romaji: 'umi' },
          { jp: 'やま', romaji: 'yama' },
        ],
        answer: { jp: 'そら', romaji: 'sora' },
      },
      {
        id: '8-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'よろしく、雉さん。',
        hiragana: ['よろしく','きじ','さん'],
        romaji: 'Yoroshiku, kiji-san.',
        en: "Nice to meet you, Mr. Pheasant.",
      },
    ]
  },
  // Day 9: The team is complete
  {
    day: 9,
    title: "The Complete Team",
    titleJp: "完璧なチーム",
    newWords: [
      { jp: 'チーム', romaji: 'chiimu' },
      { jp: 'じゅんび', romaji: 'junbi' },
      { jp: 'できました', romaji: 'dekimashita' },
      { jp: 'いよいよ', romaji: 'iyoiyo' },
    ],
    lines: [
      {
        id: '9-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'ももたろうのチームが完成しました。',
        hiragana: ['ももたろう','の','チーム','が','かんせい','しました'],
        romaji: 'Momotarou no chiimu ga kansei shimashita.',
        en: "Momotaro's team was complete.",
      },
      {
        id: '9-2',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'みんな、鬼退治の___ができました。',
        hiragana: ['みんな','おに','たいじ','の','じゅんび','が','できました'],
        romaji: 'Minna, oni taiji no ___ ga dekimashita.',
        en: "Everyone, the preparation for defeating the ogres is complete.",
        tiles: [
          { jp: 'じゅんび', romaji: 'junbi' },
          { jp: 'べんきょう', romaji: 'benkyou' },
          { jp: 'あそび', romaji: 'asobi' },
        ],
        answer: { jp: 'じゅんび', romaji: 'junbi' },
      },
      {
        id: '9-3',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___です。',
        hiragana: ['ワンワン','じゅんび','です'],
        romaji: 'Wanwan! ___ desu.',
        en: "Woof woof! We're ready.",
        tiles: [
          { jp: 'じゅんび', romaji: 'junbi' },
          { jp: 'べんきょう', romaji: 'benkyou' },
          { jp: 'あそび', romaji: 'asobi' },
        ],
        answer: { jp: 'じゅんび', romaji: 'junbi' },
      },
      {
        id: '9-4',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___鬼退治に行きましょう。',
        hiragana: ['きーきー','いよいよ','おに','たいじ','に','いきましょう'],
        romaji: 'Kiiki! ___ oni taiji ni ikimashou.',
        en: "Kee-kee! Finally, let's go defeat the ogres.",
        tiles: [
          { jp: 'いよいよ', romaji: 'iyoiyo' },
          { jp: 'また', romaji: 'mata' },
          { jp: 'すぐ', romaji: 'sugu' },
        ],
        answer: { jp: 'いよいよ', romaji: 'iyoiyo' },
      },
      {
        id: '9-5',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'normal',
        jp: 'ケンケン！行きましょう！',
        hiragana: ['ケンケン','いきましょう'],
        romaji: 'Kenken! Ikimashou!',
        en: "Ken-ken! Let's go!",
      },
    ]
  },
  // Day 10: Arriving at the island
  {
    day: 10,
    title: "The Ogre Island",
    titleJp: "鬼ヶ島",
    newWords: [
      { jp: 'とうちゃく', romaji: 'touchaku' },
      { jp: 'おにがしま', romaji: 'onigashima' },
      { jp: 'みえます', romaji: 'miemasu' },
      { jp: 'ちかづいて', romaji: 'chikazuite' },
    ],
    lines: [
      {
        id: '10-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'ももたろうたちは鬼ヶ島に到着しました。',
        hiragana: ['ももたろう','たち','は','おにがしま','に','とうちゃく','しました'],
        romaji: 'Momotarou-tachi wa onigashima ni touchaku shimashita.',
        en: "Momotaro and his friends arrived at Onigashima (Ogre Island).",
      },
      {
        id: '10-2',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'fill-blank',
        jp: 'ケンケン！___が見えます。',
        hiragana: ['ケンケン','おにがしま','が','みえます'],
        romaji: 'Kenken! ___ ga miemasu.',
        en: "Ken-ken! I can see Onigashima.",
        tiles: [
          { jp: 'おにがしま', romaji: 'onigashima' },
          { jp: 'にほん', romaji: 'nihon' },
          { jp: 'とうきょう', romaji: 'toukyou' },
        ],
        answer: { jp: 'おにがしま', romaji: 'onigashima' },
      },
      {
        id: '10-3',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: '島に___います。',
        hiragana: ['しま','に','ちかづいて','います'],
        romaji: 'Shima ni ___ imasu.',
        en: "We're approaching the island.",
        tiles: [
          { jp: 'ちかづいて', romaji: 'chikazuite' },
          { jp: 'とおざかって', romaji: 'toozakatte' },
          { jp: 'あがって', romaji: 'agatte' },
        ],
        answer: { jp: 'ちかづいて', romaji: 'chikazuite' },
      },
      {
        id: '10-4',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___しました。',
        hiragana: ['ワンワン','とうちゃく','しました'],
        romaji: 'Wanwan! ___ shimashita.',
        en: "Woof woof! We've arrived.",
        tiles: [
          { jp: 'とうちゃく', romaji: 'touchaku' },
          { jp: 'しゅっぱつ', romaji: 'shuppatsu' },
          { jp: 'かえり', romaji: 'kaeri' },
        ],
        answer: { jp: 'とうちゃく', romaji: 'touchaku' },
      },
      {
        id: '10-5',
        speaker: 'saru',
        avatar: '🐒',
        type: 'normal',
        jp: 'きーきー！気をつけましょう。',
        hiragana: ['きーきー','き','を','つけましょう'],
        romaji: 'Kiiki! Ki o tsukemashou.',
        en: "Kee-kee! Let's be careful.",
      },
    ]
  },
  // Day 11: First encounter with ogres
  {
    day: 11,
    title: "First Battle",
    titleJp: "最初の戦い",
    newWords: [
      { jp: 'たたかい', romaji: 'tatakai' },
      { jp: 'はじまり', romaji: 'hajimari' },
      { jp: 'つよい', romaji: 'tsuyoi' },
      { jp: 'がんばって', romaji: 'ganbatte' },
    ],
    lines: [
      {
        id: '11-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: '鬼ヶ島で鬼たちが待っていました。',
        hiragana: ['おにがしま','で','おに','たち','が','まって','いました'],
        romaji: 'Onigashima de oni-tachi ga matte imashita.',
        en: "The ogres were waiting on Onigashima.",
      },
      {
        id: '11-2',
        speaker: 'oni',
        avatar: '👹',
        type: 'fill-blank',
        jp: 'ガオー！___が始まりだ！',
        hiragana: ['ガオー','たたかい','が','はじまり','だ'],
        romaji: 'Gaoo! ___ ga hajimari da!',
        en: "Gao! The battle begins!",
        tiles: [
          { jp: 'たたかい', romaji: 'tatakai' },
          { jp: 'あそび', romaji: 'asobi' },
          { jp: 'べんきょう', romaji: 'benkyou' },
        ],
        answer: { jp: 'たたかい', romaji: 'tatakai' },
      },
      {
        id: '11-3',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'みんな、___で戦いましょう！',
        hiragana: ['みんな','つよく','で','たたかいましょう'],
        romaji: 'Minna, ___ de tatakaimashou!',
        en: "Everyone, let's fight strongly!",
        tiles: [
          { jp: 'つよく', romaji: 'tsuyoku' },
          { jp: 'よわく', romaji: 'yowaku' },
          { jp: 'おそく', romaji: 'osoku' },
        ],
        answer: { jp: 'つよく', romaji: 'tsuyoku' },
      },
      {
        id: '11-4',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___！',
        hiragana: ['ワンワン','がんばって'],
        romaji: 'Wanwan! ___!',
        en: "Woof woof! Do your best!",
        tiles: [
          { jp: 'がんばって', romaji: 'ganbatte' },
          { jp: 'ありがとう', romaji: 'arigatou' },
          { jp: 'さようなら', romaji: 'sayounara' },
        ],
        answer: { jp: 'がんばって', romaji: 'ganbatte' },
      },
      {
        id: '11-5',
        speaker: 'saru',
        avatar: '🐒',
        type: 'normal',
        jp: 'きーきー！頑張ります！',
        hiragana: ['きーきー','がんばります'],
        romaji: 'Kiiki! Ganbarimasu!',
        en: "Kee-kee! I'll do my best!",
      },
    ]
  },
  // Day 12: The battle intensifies
  {
    day: 12,
    title: "Intense Battle",
    titleJp: "激しい戦い",
    newWords: [
      { jp: 'げきしん', romaji: 'gekishin' },
      { jp: 'たたかって', romaji: 'tatakatte' },
      { jp: 'まけません', romaji: 'makemasen' },
      { jp: 'かつ', romaji: 'katsu' },
    ],
    lines: [
      {
        id: '12-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: '戦いが激しくなりました。',
        hiragana: ['たたかい','が','げきしく','なりました'],
        romaji: 'Tatakai ga gekishiku narimashita.',
        en: "The battle became intense.",
      },
      {
        id: '12-2',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'fill-blank',
        jp: 'ケンケン！___戦っています。',
        hiragana: ['ケンケン','げきしん','たたかって','います'],
        romaji: 'Kenken! ___ tatakatte imasu.',
        en: "Ken-ken! We're fighting intensely.",
        tiles: [
          { jp: 'げきしん', romaji: 'gekishin' },
          { jp: 'ゆっくり', romaji: 'yukkuri' },
          { jp: 'しずかに', romaji: 'shizukani' },
        ],
        answer: { jp: 'げきしん', romaji: 'gekishin' },
      },
      {
        id: '12-3',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: '負けません！必ず___！',
        hiragana: ['まけません','かならず','かつ'],
        romaji: 'Makemasen! Kanarazu ___!',
        en: "We won't lose! We will definitely win!",
        tiles: [
          { jp: 'かつ', romaji: 'katsu' },
          { jp: 'まける', romaji: 'makeru' },
          { jp: 'あきらめる', romaji: 'akirameru' },
        ],
        answer: { jp: 'かつ', romaji: 'katsu' },
      },
      {
        id: '12-4',
        speaker: 'oni',
        avatar: '👹',
        type: 'fill-blank',
        jp: 'ガオー！___！',
        hiragana: ['ガオー','まけません'],
        romaji: 'Gaoo! ___!',
        en: "Gao! We won't lose!",
        tiles: [
          { jp: 'まけません', romaji: 'makemasen' },
          { jp: 'かちません', romaji: 'kachimasen' },
          { jp: 'あきらめません', romaji: 'akiramemasen' },
        ],
        answer: { jp: 'まけません', romaji: 'makemasen' },
      },
      {
        id: '12-5',
        speaker: 'inu',
        avatar: '🐕',
        type: 'normal',
        jp: 'ワンワン！頑張れ！',
        hiragana: ['ワンワン','がんばれ'],
        romaji: 'Wanwan! Ganbare!',
        en: "Woof woof! Do your best!",
      },
    ]
  },
  // Day 13: Momotaro's strategy
  {
    day: 13,
    title: "Smart Strategy",
    titleJp: "賢い戦略",
    newWords: [
      { jp: 'せんりゃく', romaji: 'senryaku' },
      { jp: 'かんがえて', romaji: 'kangaete' },
      { jp: 'さくせん', romaji: 'sakusen' },
      { jp: 'じっこう', romaji: 'jikkou' },
    ],
    lines: [
      {
        id: '13-1',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'みんな、戦略を考えましょう。',
        hiragana: ['みんな','せんりゃく','を','かんがえましょう'],
        romaji: 'Minna, senryaku o kangaemashou.',
        en: "Everyone, let's think of a strategy.",
      },
      {
        id: '13-2',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___を考えています。',
        hiragana: ['きーきー','せんりゃく','を','かんがえて','います'],
        romaji: 'Kiiki! ___ o kangaete imasu.',
        en: "Kee-kee! I'm thinking of a strategy.",
        tiles: [
          { jp: 'せんりゃく', romaji: 'senryaku' },
          { jp: 'べんきょう', romaji: 'benkyou' },
          { jp: 'あそび', romaji: 'asobi' },
        ],
        answer: { jp: 'せんりゃく', romaji: 'senryaku' },
      },
      {
        id: '13-3',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'fill-blank',
        jp: 'ケンケン！___を実行しましょう。',
        hiragana: ['ケンケン','さくせん','を','じっこう','しましょう'],
        romaji: 'Kenken! ___ o jikkou shimashou.',
        en: "Ken-ken! Let's execute the operation.",
        tiles: [
          { jp: 'さくせん', romaji: 'sakusen' },
          { jp: 'べんきょう', romaji: 'benkyou' },
          { jp: 'あそび', romaji: 'asobi' },
        ],
        answer: { jp: 'さくせん', romaji: 'sakusen' },
      },
      {
        id: '13-4',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'はい、___します。',
        hiragana: ['はい','じっこう','します'],
        romaji: 'Hai, ___ shimasu.',
        en: "Yes, I'll execute it.",
        tiles: [
          { jp: 'じっこう', romaji: 'jikkou' },
          { jp: 'べんきょう', romaji: 'benkyou' },
          { jp: 'あそび', romaji: 'asobi' },
        ],
        answer: { jp: 'じっこう', romaji: 'jikkou' },
      },
      {
        id: '13-5',
        speaker: 'inu',
        avatar: '🐕',
        type: 'normal',
        jp: 'ワンワン！準備完了！',
        hiragana: ['ワンワン','じゅんび','かんりょう'],
        romaji: 'Wanwan! Junbi kanryou!',
        en: "Woof woof! Preparation complete!",
      },
    ]
  },
  // Day 14: The final attack
  {
    day: 14,
    title: "Final Attack",
    titleJp: "最後の攻撃",
    newWords: [
      { jp: 'さいご', romaji: 'saigo' },
      { jp: 'こうげき', romaji: 'kougeki' },
      { jp: 'いっせい', romaji: 'issei' },
      { jp: 'すすめ', romaji: 'susume' },
    ],
    lines: [
      {
        id: '14-1',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: '最後の攻撃をしましょう！',
        hiragana: ['さいご','の','こうげき','を','しましょう'],
        romaji: 'Saigo no kougeki o shimashou!',
        en: "Let's make the final attack!",
      },
      {
        id: '14-2',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___の攻撃です！',
        hiragana: ['ワンワン','いっせい','の','こうげき','です'],
        romaji: 'Wanwan! ___ no kougeki desu!',
        en: "Woof woof! It's a simultaneous attack!",
        tiles: [
          { jp: 'いっせい', romaji: 'issei' },
          { jp: 'ひとり', romaji: 'hitori' },
          { jp: 'ふたり', romaji: 'futari' },
        ],
        answer: { jp: 'いっせい', romaji: 'issei' },
      },
      {
        id: '14-3',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___！',
        hiragana: ['きーきー','すすめ'],
        romaji: 'Kiiki! ___!',
        en: "Kee-kee! Advance!",
        tiles: [
          { jp: 'すすめ', romaji: 'susume' },
          { jp: 'とまれ', romaji: 'tomare' },
          { jp: 'かえれ', romaji: 'kaere' },
        ],
        answer: { jp: 'すすめ', romaji: 'susume' },
      },
      {
        id: '14-4',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'fill-blank',
        jp: 'ケンケン！___！',
        hiragana: ['ケンケン','すすめ'],
        romaji: 'Kenken! ___!',
        en: "Ken-ken! Advance!",
        tiles: [
          { jp: 'すすめ', romaji: 'susume' },
          { jp: 'とまれ', romaji: 'tomare' },
          { jp: 'かえれ', romaji: 'kaere' },
        ],
        answer: { jp: 'すすめ', romaji: 'susume' },
      },
      {
        id: '14-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'みんな、突撃！',
        hiragana: ['みんな','とつげき'],
        romaji: 'Minna, totsugeki!',
        en: "Everyone, charge!",
      },
    ]
  },
  // Day 15: Victory achieved
  {
    day: 15,
    title: "Victory!",
    titleJp: "勝利！",
    newWords: [
      { jp: 'しょうり', romaji: 'shouri' },
      { jp: 'かちました', romaji: 'kachimashita' },
      { jp: 'おに', romaji: 'oni' },
      { jp: 'まけました', romaji: 'makemashita' },
    ],
    lines: [
      {
        id: '15-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'ももたろうたちの勝利でした！',
        hiragana: ['ももたろう','たち','の','しょうり','でした'],
        romaji: 'Momotarou-tachi no shouri deshita!',
        en: "It was Momotaro and his friends' victory!",
      },
      {
        id: '15-2',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'やった！___しました！',
        hiragana: ['やった','かちました'],
        romaji: 'Yatta! ___ shimashita!',
        en: "Yay! We won!",
        tiles: [
          { jp: 'かちました', romaji: 'kachimashita' },
          { jp: 'まけました', romaji: 'makemashita' },
          { jp: 'あきらめました', romaji: 'akiramemashita' },
        ],
        answer: { jp: 'かちました', romaji: 'kachimashita' },
      },
      {
        id: '15-3',
        speaker: 'oni',
        avatar: '👹',
        type: 'fill-blank',
        jp: 'ガオー...___。',
        hiragana: ['ガオー','まけました'],
        romaji: 'Gaoo... ___.',
        en: "Gao... We lost.",
        tiles: [
          { jp: 'まけました', romaji: 'makemashita' },
          { jp: 'かちました', romaji: 'kachimashita' },
          { jp: 'あきらめました', romaji: 'akiramemashita' },
        ],
        answer: { jp: 'まけました', romaji: 'makemashita' },
      },
      {
        id: '15-4',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___！',
        hiragana: ['ワンワン','しょうり'],
        romaji: 'Wanwan! ___!',
        en: "Woof woof! Victory!",
        tiles: [
          { jp: 'しょうり', romaji: 'shouri' },
          { jp: 'まけ', romaji: 'make' },
          { jp: 'あきらめ', romaji: 'akirame' },
        ],
        answer: { jp: 'しょうり', romaji: 'shouri' },
      },
      {
        id: '15-5',
        speaker: 'saru',
        avatar: '🐒',
        type: 'normal',
        jp: 'きーきー！やったー！',
        hiragana: ['きーきー','やったー'],
        romaji: 'Kiiki! Yattaa!',
        en: "Kee-kee! Yay!",
      },
    ]
  },
  // Day 16: Collecting treasure
  {
    day: 16,
    title: "The Treasure",
    titleJp: "宝物",
    newWords: [
      { jp: 'たからもの', romaji: 'takaramono' },
      { jp: 'きん', romaji: 'kin' },
      { jp: 'ぎん', romaji: 'gin' },
      { jp: 'みつけました', romaji: 'mitsukemashita' },
    ],
    lines: [
      {
        id: '16-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: '鬼の城には宝物がたくさんありました。',
        hiragana: ['おに','の','しろ','には','たからもの','が','たくさん','ありました'],
        romaji: 'Oni no shiro ni wa takaramono ga takusan arimashita.',
        en: "There were many treasures in the ogre's castle.",
      },
      {
        id: '16-2',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'すごい！___がたくさんあります。',
        hiragana: ['すごい','たからもの','が','たくさん','あります'],
        romaji: 'Sugoi! ___ ga takusan arimasu.',
        en: "Amazing! There are many treasures.",
        tiles: [
          { jp: 'たからもの', romaji: 'takaramono' },
          { jp: 'おもちゃ', romaji: 'omocha' },
          { jp: 'ほん', romaji: 'hon' },
        ],
        answer: { jp: 'たからもの', romaji: 'takaramono' },
      },
      {
        id: '16-3',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___を見つけました！',
        hiragana: ['ワンワン','きん','を','みつけました'],
        romaji: 'Wanwan! ___ o mitsukemashita!',
        en: "Woof woof! I found gold!",
        tiles: [
          { jp: 'きん', romaji: 'kin' },
          { jp: 'ぎん', romaji: 'gin' },
          { jp: 'どう', romaji: 'dou' },
        ],
        answer: { jp: 'きん', romaji: 'kin' },
      },
      {
        id: '16-4',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___もあります！',
        hiragana: ['きーきー','ぎん','も','あります'],
        romaji: 'Kiiki! ___ mo arimasu!',
        en: "Kee-kee! There's silver too!",
        tiles: [
          { jp: 'ぎん', romaji: 'gin' },
          { jp: 'きん', romaji: 'kin' },
          { jp: 'どう', romaji: 'dou' },
        ],
        answer: { jp: 'ぎん', romaji: 'gin' },
      },
      {
        id: '16-5',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'normal',
        jp: 'ケンケン！宝物を集めましょう！',
        hiragana: ['ケンケン','たからもの','を','あつめましょう'],
        romaji: 'Kenken! Takaramono o atsumemashou!',
        en: "Ken-ken! Let's collect the treasures!",
      },
    ]
  },
  // Day 17: Preparing to return
  {
    day: 17,
    title: "Return Journey",
    titleJp: "帰りの旅",
    newWords: [
      { jp: 'かえり', romaji: 'kaeri' },
      { jp: 'じゅんび', romaji: 'junbi' },
      { jp: 'おみやげ', romaji: 'omiyage' },
      { jp: 'もって', romaji: 'motte' },
    ],
    lines: [
      {
        id: '17-1',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'みんな、帰りの準備をしましょう。',
        hiragana: ['みんな','かえり','の','じゅんび','を','しましょう'],
        romaji: 'Minna, kaeri no junbi o shimashou.',
        en: "Everyone, let's prepare for the return journey.",
      },
      {
        id: '17-2',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___を持って帰りましょう。',
        hiragana: ['ワンワン','おみやげ','を','もって','かえりましょう'],
        romaji: 'Wanwan! ___ o motte kaerimashou.',
        en: "Woof woof! Let's take souvenirs home.",
        tiles: [
          { jp: 'おみやげ', romaji: 'omiyage' },
          { jp: 'たからもの', romaji: 'takaramono' },
          { jp: 'きびだんご', romaji: 'kibidango' },
        ],
        answer: { jp: 'おみやげ', romaji: 'omiyage' },
      },
      {
        id: '17-3',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___を運びます。',
        hiragana: ['きーきー','たからもの','を','はこびます'],
        romaji: 'Kiiki! ___ o hakobimasu.',
        en: "Kee-kee! I'll carry the treasures.",
        tiles: [
          { jp: 'たからもの', romaji: 'takaramono' },
          { jp: 'おみやげ', romaji: 'omiyage' },
          { jp: 'きびだんご', romaji: 'kibidango' },
        ],
        answer: { jp: 'たからもの', romaji: 'takaramono' },
      },
      {
        id: '17-4',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'fill-blank',
        jp: 'ケンケン！空から___見えます。',
        hiragana: ['ケンケン','そら','から','みえます'],
        romaji: 'Kenken! Sora kara ___ miemasu.',
        en: "Ken-ken! I can see from the sky.",
        tiles: [
          { jp: 'みえます', romaji: 'miemasu' },
          { jp: 'きこえます', romaji: 'kikoemasu' },
          { jp: 'わかります', romaji: 'wakarimasu' },
        ],
        answer: { jp: 'みえます', romaji: 'miemasu' },
      },
      {
        id: '17-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'よろしく、みんな。',
        hiragana: ['よろしく','みんな'],
        romaji: 'Yoroshiku, minna.',
        en: "Thank you, everyone.",
      },
    ]
  },
  // Day 18: The journey home
  {
    day: 18,
    title: "Homeward Bound",
    titleJp: "故郷へ",
    newWords: [
      { jp: 'ふるさと', romaji: 'furusato' },
      { jp: 'むら', romaji: 'mura' },
      { jp: 'かえって', romaji: 'kaette' },
      { jp: 'まち', romaji: 'machi' },
    ],
    lines: [
      {
        id: '18-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'ももたろうたちは故郷の村に向かいました。',
        hiragana: ['ももたろう','たち','は','ふるさと','の','むら','に','むかいました'],
        romaji: 'Momotarou-tachi wa furusato no mura ni mukaimashita.',
        en: "Momotaro and his friends headed toward their hometown village.",
      },
      {
        id: '18-2',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: '___に帰ります。',
        hiragana: ['ふるさと','に','かえります'],
        romaji: '___ ni kaerimasu.',
        en: "I'm returning to my hometown.",
        tiles: [
          { jp: 'ふるさと', romaji: 'furusato' },
          { jp: 'むら', romaji: 'mura' },
          { jp: 'まち', romaji: 'machi' },
        ],
        answer: { jp: 'ふるさと', romaji: 'furusato' },
      },
      {
        id: '18-3',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___が見えます。',
        hiragana: ['ワンワン','むら','が','みえます'],
        romaji: 'Wanwan! ___ ga miemasu.',
        en: "Woof woof! I can see the village.",
        tiles: [
          { jp: 'むら', romaji: 'mura' },
          { jp: 'まち', romaji: 'machi' },
          { jp: 'ふるさと', romaji: 'furusato' },
        ],
        answer: { jp: 'むら', romaji: 'mura' },
      },
      {
        id: '18-4',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___に着きました。',
        hiragana: ['きーきー','むら','に','つきました'],
        romaji: 'Kiiki! ___ ni tsukimashita.',
        en: "Kee-kee! We've arrived at the village.",
        tiles: [
          { jp: 'むら', romaji: 'mura' },
          { jp: 'まち', romaji: 'machi' },
          { jp: 'ふるさと', romaji: 'furusato' },
        ],
        answer: { jp: 'むら', romaji: 'mura' },
      },
      {
        id: '18-5',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'normal',
        jp: 'ケンケン！故郷に帰りました！',
        hiragana: ['ケンケン','ふるさと','に','かえりました'],
        romaji: 'Kenken! Furusato ni kaerimashita!',
        en: "Ken-ken! We've returned to our hometown!",
      },
    ]
  },
  // Day 19: Reunion with grandparents
  {
    day: 19,
    title: "Happy Reunion",
    titleJp: "幸せな再会",
    newWords: [
      { jp: 'さいかい', romaji: 'saikai' },
      { jp: 'よろこび', romaji: 'yorokobi' },
      { jp: 'なみだ', romaji: 'namida' },
      { jp: 'だきしめて', romaji: 'dakishimete' },
    ],
    lines: [
      {
        id: '19-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'おじいさんとおばあさんが待っていました。',
        hiragana: ['おじいさん','と','おばあさん','が','まって','いました'],
        romaji: 'Ojiisan to obaasan ga matte imashita.',
        en: "Grandfather and grandmother were waiting.",
      },
      {
        id: '19-2',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: 'ももたろう！___です！',
        hiragana: ['ももたろう','さいかい','です'],
        romaji: 'Momotarou! ___ desu!',
        en: "Momotaro! It's a reunion!",
        tiles: [
          { jp: 'さいかい', romaji: 'saikai' },
          { jp: 'わかれ', romaji: 'wakare' },
          { jp: 'はじまり', romaji: 'hajimari' },
        ],
        answer: { jp: 'さいかい', romaji: 'saikai' },
      },
      {
        id: '19-3',
        speaker: 'ojiisan',
        avatar: '👴',
        type: 'fill-blank',
        jp: '無事で___です。',
        hiragana: ['ぶじ','で','よろこび','です'],
        romaji: 'Buji de ___ desu.',
        en: "I'm happy you're safe.",
        tiles: [
          { jp: 'よろこび', romaji: 'yorokobi' },
          { jp: 'かなしみ', romaji: 'kanashimi' },
          { jp: 'おこり', romaji: 'okori' },
        ],
        answer: { jp: 'よろこび', romaji: 'yorokobi' },
      },
      {
        id: '19-4',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'おばあさん、___で抱きしめてください。',
        hiragana: ['おばあさん','なみだ','で','だきしめて','ください'],
        romaji: 'Obaasan, ___ de dakishimete kudasai.',
        en: "Grandmother, please hug me with tears.",
        tiles: [
          { jp: 'なみだ', romaji: 'namida' },
          { jp: 'よろこび', romaji: 'yorokobi' },
          { jp: 'わらい', romaji: 'warai' },
        ],
        answer: { jp: 'なみだ', romaji: 'namida' },
      },
      {
        id: '19-5',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'normal',
        jp: 'はい、抱きしめます。',
        hiragana: ['はい','だきしめます'],
        romaji: 'Hai, dakishimemasu.',
        en: "Yes, I'll hug you.",
      },
    ]
  },
  // Day 20: Sharing the treasure
  {
    day: 20,
    title: "Sharing the Treasure",
    titleJp: "宝物の共有",
    newWords: [
      { jp: 'わかちあい', romaji: 'wakachiai' },
      { jp: 'みんな', romaji: 'minna' },
      { jp: 'たからもの', romaji: 'takaramono' },
      { jp: 'ありがとう', romaji: 'arigatou' },
    ],
    lines: [
      {
        id: '20-1',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'おじいさん、おばあさん、宝物を分け合いましょう。',
        hiragana: ['おじいさん','おばあさん','たからもの','を','わけあいましょう'],
        romaji: 'Ojiisan, obaasan, takaramono o wakeaimashou.',
        en: "Grandfather, grandmother, let's share the treasures.",
      },
      {
        id: '20-2',
        speaker: 'ojiisan',
        avatar: '👴',
        type: 'fill-blank',
        jp: 'みんなで___しましょう。',
        hiragana: ['みんな','で','わかちあい','しましょう'],
        romaji: 'Minna de ___ shimashou.',
        en: "Let's share with everyone.",
        tiles: [
          { jp: 'わかちあい', romaji: 'wakachiai' },
          { jp: 'べんきょう', romaji: 'benkyou' },
          { jp: 'あそび', romaji: 'asobi' },
        ],
        answer: { jp: 'わかちあい', romaji: 'wakachiai' },
      },
      {
        id: '20-3',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: '___にありがとう。',
        hiragana: ['みんな','に','ありがとう'],
        romaji: '___ ni arigatou.',
        en: "Thank you to everyone.",
        tiles: [
          { jp: 'みんな', romaji: 'minna' },
          { jp: 'おじいさん', romaji: 'ojiisan' },
          { jp: 'おばあさん', romaji: 'obaasan' },
        ],
        answer: { jp: 'みんな', romaji: 'minna' },
      },
      {
        id: '20-4',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___を運びます。',
        hiragana: ['ワンワン','たからもの','を','はこびます'],
        romaji: 'Wanwan! ___ o hakobimasu.',
        en: "Woof woof! I'll carry the treasures.",
        tiles: [
          { jp: 'たからもの', romaji: 'takaramono' },
          { jp: 'おみやげ', romaji: 'omiyage' },
          { jp: 'きびだんご', romaji: 'kibidango' },
        ],
        answer: { jp: 'たからもの', romaji: 'takaramono' },
      },
      {
        id: '20-5',
        speaker: 'saru',
        avatar: '🐒',
        type: 'normal',
        jp: 'きーきー！ありがとうございます！',
        hiragana: ['きーきー','ありがとう','ございます'],
        romaji: 'Kiiki! Arigatou gozaimasu!',
        en: "Kee-kee! Thank you very much!",
      },
    ]
  },
  // Day 21: Village celebration
  {
    day: 21,
    title: "Village Celebration",
    titleJp: "村の祝い",
    newWords: [
      { jp: 'いわい', romaji: 'iwai' },
      { jp: 'おまつり', romaji: 'omatsuri' },
      { jp: 'たのしい', romaji: 'tanoshii' },
      { jp: 'おどって', romaji: 'odotte' },
    ],
    lines: [
      {
        id: '21-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: '村中でお祝いの祭りが始まりました。',
        hiragana: ['むらじゅう','で','おいわい','の','まつり','が','はじまりました'],
        romaji: 'Murajuu de oiwai no matsuri ga hajimarimashita.',
        en: "A celebration festival began throughout the village.",
      },
      {
        id: '21-2',
        speaker: 'villager',
        avatar: '👨',
        type: 'fill-blank',
        jp: 'ももたろう、___です！',
        hiragana: ['ももたろう','いわい','です'],
        romaji: 'Momotarou, ___ desu!',
        en: "Momotaro, it's a celebration!",
        tiles: [
          { jp: 'いわい', romaji: 'iwai' },
          { jp: 'おまつり', romaji: 'omatsuri' },
          { jp: 'たのしい', romaji: 'tanoshii' },
        ],
        answer: { jp: 'いわい', romaji: 'iwai' },
      },
      {
        id: '21-3',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'みんなで___しましょう！',
        hiragana: ['みんな','で','おまつり','しましょう'],
        romaji: 'Minna de ___ shimashou!',
        en: "Let's have a festival together!",
        tiles: [
          { jp: 'おまつり', romaji: 'omatsuri' },
          { jp: 'いわい', romaji: 'iwai' },
          { jp: 'たのしい', romaji: 'tanoshii' },
        ],
        answer: { jp: 'おまつり', romaji: 'omatsuri' },
      },
      {
        id: '21-4',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___です！',
        hiragana: ['ワンワン','たのしい','です'],
        romaji: 'Wanwan! ___ desu!',
        en: "Woof woof! It's fun!",
        tiles: [
          { jp: 'たのしい', romaji: 'tanoshii' },
          { jp: 'かなしい', romaji: 'kanashii' },
          { jp: 'おもしろい', romaji: 'omoshiroi' },
        ],
        answer: { jp: 'たのしい', romaji: 'tanoshii' },
      },
      {
        id: '21-5',
        speaker: 'saru',
        avatar: '🐒',
        type: 'normal',
        jp: 'きーきー！踊りましょう！',
        hiragana: ['きーきー','おどりましょう'],
        romaji: 'Kiiki! Odorimashou!',
        en: "Kee-kee! Let's dance!",
      },
    ]
  },
  // Day 22: Lessons learned
  {
    day: 22,
    title: "Lessons Learned",
    titleJp: "学んだ教訓",
    newWords: [
      { jp: 'きょうくん', romaji: 'kyoukun' },
      { jp: 'ゆうき', romaji: 'yuuki' },
      { jp: 'ゆうじょう', romaji: 'yuujou' },
      { jp: 'きょうりょく', romaji: 'kyouryoku' },
    ],
    lines: [
      {
        id: '22-1',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'この旅で大切な教訓を学びました。',
        hiragana: ['この','たび','で','たいせつ','な','きょうくん','を','まなびました'],
        romaji: 'Kono tabi de taisetsu na kyoukun o manabimashita.',
        en: "I learned important lessons from this journey.",
      },
      {
        id: '22-2',
        speaker: 'ojiisan',
        avatar: '👴',
        type: 'fill-blank',
        jp: 'ももたろう、___が大切です。',
        hiragana: ['ももたろう','ゆうき','が','たいせつ','です'],
        romaji: 'Momotarou, ___ ga taisetsu desu.',
        en: "Momotaro, courage is important.",
        tiles: [
          { jp: 'ゆうき', romaji: 'yuuki' },
          { jp: 'ゆうじょう', romaji: 'yuujou' },
          { jp: 'きょうりょく', romaji: 'kyouryoku' },
        ],
        answer: { jp: 'ゆうき', romaji: 'yuuki' },
      },
      {
        id: '22-3',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: '___も大切です。',
        hiragana: ['ゆうじょう','も','たいせつ','です'],
        romaji: '___ mo taisetsu desu.',
        en: "Friendship is also important.",
        tiles: [
          { jp: 'ゆうじょう', romaji: 'yuujou' },
          { jp: 'ゆうき', romaji: 'yuuki' },
          { jp: 'きょうりょく', romaji: 'kyouryoku' },
        ],
        answer: { jp: 'ゆうじょう', romaji: 'yuujou' },
      },
      {
        id: '22-4',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'みんなの___で勝ちました。',
        hiragana: ['みんな','の','きょうりょく','で','かちました'],
        romaji: 'Minna no ___ de kachimashita.',
        en: "We won with everyone's cooperation.",
        tiles: [
          { jp: 'きょうりょく', romaji: 'kyouryoku' },
          { jp: 'ゆうき', romaji: 'yuuki' },
          { jp: 'ゆうじょう', romaji: 'yuujou' },
        ],
        answer: { jp: 'きょうりょく', romaji: 'kyouryoku' },
      },
      {
        id: '22-5',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'normal',
        jp: 'ケンケン！協力は大切です！',
        hiragana: ['ケンケン','きょうりょく','は','たいせつ','です'],
        romaji: 'Kenken! Kyouryoku wa taisetsu desu!',
        en: "Ken-ken! Cooperation is important!",
      },
    ]
  },
  // Day 23: New adventures await
  {
    day: 23,
    title: "New Adventures",
    titleJp: "新しい冒険",
    newWords: [
      { jp: 'あたらしい', romaji: 'atarashii' },
      { jp: 'ぼうけん', romaji: 'bouken' },
      { jp: 'まって', romaji: 'matte' },
      { jp: 'ゆめ', romaji: 'yume' },
    ],
    lines: [
      {
        id: '23-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'ももたろうは新しい冒険を夢見ていました。',
        hiragana: ['ももたろう','は','あたらしい','ぼうけん','を','ゆめみて','いました'],
        romaji: 'Momotarou wa atarashii bouken o yumemite imashita.',
        en: "Momotaro was dreaming of new adventures.",
      },
      {
        id: '23-2',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: '___冒険が待っています。',
        hiragana: ['あたらしい','ぼうけん','が','まって','います'],
        romaji: '___ bouken ga matte imasu.',
        en: "New adventures are waiting.",
        tiles: [
          { jp: 'あたらしい', romaji: 'atarashii' },
          { jp: 'ふるい', romaji: 'furui' },
          { jp: 'おおきい', romaji: 'ookii' },
        ],
        answer: { jp: 'あたらしい', romaji: 'atarashii' },
      },
      {
        id: '23-3',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___に行きましょう！',
        hiragana: ['ワンワン','ぼうけん','に','いきましょう'],
        romaji: 'Wanwan! ___ ni ikimashou!',
        en: "Woof woof! Let's go on an adventure!",
        tiles: [
          { jp: 'ぼうけん', romaji: 'bouken' },
          { jp: 'たび', romaji: 'tabi' },
          { jp: 'あそび', romaji: 'asobi' },
        ],
        answer: { jp: 'ぼうけん', romaji: 'bouken' },
      },
      {
        id: '23-4',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___を追いかけましょう！',
        hiragana: ['きーきー','ゆめ','を','おいかけましょう'],
        romaji: 'Kiiki! ___ o oikakemashou!',
        en: "Kee-kee! Let's chase our dreams!",
        tiles: [
          { jp: 'ゆめ', romaji: 'yume' },
          { jp: 'ぼうけん', romaji: 'bouken' },
          { jp: 'たび', romaji: 'tabi' },
        ],
        answer: { jp: 'ゆめ', romaji: 'yume' },
      },
      {
        id: '23-5',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'normal',
        jp: 'ケンケン！夢に向かって飛びましょう！',
        hiragana: ['ケンケン','ゆめ','に','むかって','とびましょう'],
        romaji: 'Kenken! Yume ni mukatte tobimashou!',
        en: "Ken-ken! Let's fly toward our dreams!",
      },
    ]
  },
  // Day 24: Growing stronger
  {
    day: 24,
    title: "Growing Stronger",
    titleJp: "より強く",
    newWords: [
      { jp: 'つよくなり', romaji: 'tsuyokunari' },
      { jp: 'かしこく', romaji: 'kashikoku' },
      { jp: 'やさしく', romaji: 'yasashiku' },
      { jp: 'そだって', romaji: 'sodatte' },
    ],
    lines: [
      {
        id: '24-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'ももたろうは毎日成長していました。',
        hiragana: ['ももたろう','は','まいにち','せいちょう','して','いました'],
        romaji: 'Momotarou wa mainichi seichou shite imashita.',
        en: "Momotaro was growing every day.",
      },
      {
        id: '24-2',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: '毎日___います。',
        hiragana: ['まいにち','つよくなり','います'],
        romaji: 'Mainichi ___ imasu.',
        en: "I'm getting stronger every day.",
        tiles: [
          { jp: 'つよくなり', romaji: 'tsuyokunari' },
          { jp: 'かしこく', romaji: 'kashikoku' },
          { jp: 'やさしく', romaji: 'yasashiku' },
        ],
        answer: { jp: 'つよくなり', romaji: 'tsuyokunari' },
      },
      {
        id: '24-3',
        speaker: 'ojiisan',
        avatar: '👴',
        type: 'fill-blank',
        jp: '___も大切です。',
        hiragana: ['かしこく','も','たいせつ','です'],
        romaji: '___ mo taisetsu desu.',
        en: "Being wise is also important.",
        tiles: [
          { jp: 'かしこく', romaji: 'kashikoku' },
          { jp: 'つよく', romaji: 'tsuyoku' },
          { jp: 'やさしく', romaji: 'yasashiku' },
        ],
        answer: { jp: 'かしこく', romaji: 'kashikoku' },
      },
      {
        id: '24-4',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: '___な心も大切です。',
        hiragana: ['やさしく','な','こころ','も','たいせつ','です'],
        romaji: '___ na kokoro mo taisetsu desu.',
        en: "A kind heart is also important.",
        tiles: [
          { jp: 'やさしく', romaji: 'yasashiku' },
          { jp: 'つよく', romaji: 'tsuyoku' },
          { jp: 'かしこく', romaji: 'kashikoku' },
        ],
        answer: { jp: 'やさしく', romaji: 'yasashiku' },
      },
      {
        id: '24-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'はい、成長します。',
        hiragana: ['はい','せいちょう','します'],
        romaji: 'Hai, seichou shimasu.',
        en: "Yes, I will grow.",
      },
    ]
  },
  // Day 25: Helping others
  {
    day: 25,
    title: "Helping Others",
    titleJp: "人助け",
    newWords: [
      { jp: 'ひとだすけ', romaji: 'hitodasuke' },
      { jp: 'たすけて', romaji: 'tasukete' },
      { jp: 'しあわせ', romaji: 'shiawase' },
      { jp: 'よろこび', romaji: 'yorokobi' },
    ],
    lines: [
      {
        id: '25-1',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: '困っている人を助けることが大切です。',
        hiragana: ['こまって','いる','ひと','を','たすける','こと','が','たいせつ','です'],
        romaji: 'Komatte iru hito o tasukeru koto ga taisetsu desu.',
        en: "It's important to help people in trouble.",
      },
      {
        id: '25-2',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___をしましょう！',
        hiragana: ['ワンワン','ひとだすけ','を','しましょう'],
        romaji: 'Wanwan! ___ o shimashou!',
        en: "Woof woof! Let's help people!",
        tiles: [
          { jp: 'ひとだすけ', romaji: 'hitodasuke' },
          { jp: 'たすけて', romaji: 'tasukete' },
          { jp: 'しあわせ', romaji: 'shiawase' },
        ],
        answer: { jp: 'ひとだすけ', romaji: 'hitodasuke' },
      },
      {
        id: '25-3',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！みんなを___！',
        hiragana: ['きーきー','みんな','を','たすけて'],
        romaji: 'Kiiki! Minna o ___!',
        en: "Kee-kee! Let's help everyone!",
        tiles: [
          { jp: 'たすけて', romaji: 'tasukete' },
          { jp: 'しあわせ', romaji: 'shiawase' },
          { jp: 'よろこび', romaji: 'yorokobi' },
        ],
        answer: { jp: 'たすけて', romaji: 'tasukete' },
      },
      {
        id: '25-4',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'fill-blank',
        jp: 'ケンケン！___を作りましょう！',
        hiragana: ['ケンケン','しあわせ','を','つくりましょう'],
        romaji: 'Kenken! ___ o tsukurimashou!',
        en: "Ken-ken! Let's create happiness!",
        tiles: [
          { jp: 'しあわせ', romaji: 'shiawase' },
          { jp: 'よろこび', romaji: 'yorokobi' },
          { jp: 'たのしみ', romaji: 'tanoshimi' },
        ],
        answer: { jp: 'しあわせ', romaji: 'shiawase' },
      },
      {
        id: '25-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: 'みんなの喜びが私の喜びです。',
        hiragana: ['みんな','の','よろこび','が','わたし','の','よろこび','です'],
        romaji: 'Minna no yorokobi ga watashi no yorokobi desu.',
        en: "Everyone's joy is my joy.",
      },
    ]
  },
  // Day 26: The power of friendship
  {
    day: 26,
    title: "Friendship Power",
    titleJp: "友情の力",
    newWords: [
      { jp: 'ゆうじょう', romaji: 'yuujou' },
      { jp: 'ちから', romaji: 'chikara' },
      { jp: 'ともだち', romaji: 'tomodachi' },
      { jp: 'いっしょに', romaji: 'issho ni' },
    ],
    lines: [
      {
        id: '26-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: '友情の力はとても強いです。',
        hiragana: ['ゆうじょう','の','ちから','は','とても','つよい','です'],
        romaji: 'Yuujou no chikara wa totemo tsuyoi desu.',
        en: "The power of friendship is very strong.",
      },
      {
        id: '26-2',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: '___の力で勝ちました。',
        hiragana: ['ゆうじょう','の','ちから','で','かちました'],
        romaji: '___ no chikara de kachimashita.',
        en: "We won with the power of friendship.",
        tiles: [
          { jp: 'ゆうじょう', romaji: 'yuujou' },
          { jp: 'ちから', romaji: 'chikara' },
          { jp: 'ともだち', romaji: 'tomodachi' },
        ],
        answer: { jp: 'ゆうじょう', romaji: 'yuujou' },
      },
      {
        id: '26-3',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___が大切です！',
        hiragana: ['ワンワン','ともだち','が','たいせつ','です'],
        romaji: 'Wanwan! ___ ga taisetsu desu!',
        en: "Woof woof! Friends are important!",
        tiles: [
          { jp: 'ともだち', romaji: 'tomodachi' },
          { jp: 'ゆうじょう', romaji: 'yuujou' },
          { jp: 'ちから', romaji: 'chikara' },
        ],
        answer: { jp: 'ともだち', romaji: 'tomodachi' },
      },
      {
        id: '26-4',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___に頑張りましょう！',
        hiragana: ['きーきー','いっしょに','がんばりましょう'],
        romaji: 'Kiiki! ___ ni ganbarimashou!',
        en: "Kee-kee! Let's do our best together!",
        tiles: [
          { jp: 'いっしょに', romaji: 'issho ni' },
          { jp: 'ひとりで', romaji: 'hitori de' },
          { jp: 'あとで', romaji: 'ato de' },
        ],
        answer: { jp: 'いっしょに', romaji: 'issho ni' },
      },
      {
        id: '26-5',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'normal',
        jp: 'ケンケン！友情万歳！',
        hiragana: ['ケンケン','ゆうじょう','ばんざい'],
        romaji: 'Kenken! Yuujou banzai!',
        en: "Ken-ken! Long live friendship!",
      },
    ]
  },
  // Day 27: Never give up
  {
    day: 27,
    title: "Never Give Up",
    titleJp: "諦めない",
    newWords: [
      { jp: 'あきらめない', romaji: 'akiramenai' },
      { jp: 'がんばる', romaji: 'ganbaru' },
      { jp: 'しんじる', romaji: 'shinjiru' },
      { jp: 'せいこう', romaji: 'seikou' },
    ],
    lines: [
      {
        id: '27-1',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: '大切なことは諦めないことです。',
        hiragana: ['たいせつ','な','こと','は','あきらめない','こと','です'],
        romaji: 'Taisetsu na koto wa akiramenai koto desu.',
        en: "The important thing is to never give up.",
      },
      {
        id: '27-2',
        speaker: 'ojiisan',
        avatar: '👴',
        type: 'fill-blank',
        jp: 'ももたろう、___で頑張りました。',
        hiragana: ['ももたろう','あきらめない','で','がんばりました'],
        romaji: 'Momotarou, ___ de ganbarimashita.',
        en: "Momotaro, you persevered without giving up.",
        tiles: [
          { jp: 'あきらめない', romaji: 'akiramenai' },
          { jp: 'がんばる', romaji: 'ganbaru' },
          { jp: 'しんじる', romaji: 'shinjiru' },
        ],
        answer: { jp: 'あきらめない', romaji: 'akiramenai' },
      },
      {
        id: '27-3',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'fill-blank',
        jp: '___ことが大切です。',
        hiragana: ['がんばる','こと','が','たいせつ','です'],
        romaji: '___ koto ga taisetsu desu.',
        en: "It's important to do your best.",
        tiles: [
          { jp: 'がんばる', romaji: 'ganbaru' },
          { jp: 'あきらめる', romaji: 'akirameru' },
          { jp: 'しんじる', romaji: 'shinjiru' },
        ],
        answer: { jp: 'がんばる', romaji: 'ganbaru' },
      },
      {
        id: '27-4',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: '自分を___ことが大切です。',
        hiragana: ['じぶん','を','しんじる','こと','が','たいせつ','です'],
        romaji: 'Jibun o ___ koto ga taisetsu desu.',
        en: "It's important to believe in yourself.",
        tiles: [
          { jp: 'しんじる', romaji: 'shinjiru' },
          { jp: 'あきらめる', romaji: 'akirameru' },
          { jp: 'がんばる', romaji: 'ganbaru' },
        ],
        answer: { jp: 'しんじる', romaji: 'shinjiru' },
      },
      {
        id: '27-5',
        speaker: 'inu',
        avatar: '🐕',
        type: 'normal',
        jp: 'ワンワン！成功します！',
        hiragana: ['ワンワン','せいこう','します'],
        romaji: 'Wanwan! Seikou shimasu!',
        en: "Woof woof! We'll succeed!",
      },
    ]
  },
  // Day 28: The legacy
  {
    day: 28,
    title: "The Legacy",
    titleJp: "伝説",
    newWords: [
      { jp: 'でんせつ', romaji: 'densetsu' },
      { jp: 'ものがたり', romaji: 'monogatari' },
      { jp: 'つたえて', romaji: 'tsutaete' },
      { jp: 'おぼえて', romaji: 'oboete' },
    ],
    lines: [
      {
        id: '28-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'ももたろうの伝説は今も語り継がれています。',
        hiragana: ['ももたろう','の','でんせつ','は','いま','も','かたりつがれて','います'],
        romaji: 'Momotarou no densetsu wa ima mo katari tsugarete imasu.',
        en: "Momotaro's legend is still being told today.",
      },
      {
        id: '28-2',
        speaker: 'villager',
        avatar: '👨',
        type: 'fill-blank',
        jp: 'この___を覚えています。',
        hiragana: ['この','ものがたり','を','おぼえて','います'],
        romaji: 'Kono ___ o oboete imasu.',
        en: "I remember this story.",
        tiles: [
          { jp: 'ものがたり', romaji: 'monogatari' },
          { jp: 'でんせつ', romaji: 'densetsu' },
          { jp: 'ゆめ', romaji: 'yume' },
        ],
        answer: { jp: 'ものがたり', romaji: 'monogatari' },
      },
      {
        id: '28-3',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'この___を伝えてください。',
        hiragana: ['この','でんせつ','を','つたえて','ください'],
        romaji: 'Kono ___ o tsutaete kudasai.',
        en: "Please pass on this legend.",
        tiles: [
          { jp: 'でんせつ', romaji: 'densetsu' },
          { jp: 'ものがたり', romaji: 'monogatari' },
          { jp: 'ゆめ', romaji: 'yume' },
        ],
        answer: { jp: 'でんせつ', romaji: 'densetsu' },
      },
      {
        id: '28-4',
        speaker: 'ojiisan',
        avatar: '👴',
        type: 'fill-blank',
        jp: 'みんなに___ましょう。',
        hiragana: ['みんな','に','つたえて','ましょう'],
        romaji: 'Minna ni ___ mashou.',
        en: "Let's tell everyone.",
        tiles: [
          { jp: 'つたえて', romaji: 'tsutaete' },
          { jp: 'おぼえて', romaji: 'oboete' },
          { jp: 'きいて', romaji: 'kiite' },
        ],
        answer: { jp: 'つたえて', romaji: 'tsutaete' },
      },
      {
        id: '28-5',
        speaker: 'obaasan',
        avatar: '👵',
        type: 'normal',
        jp: '大切な物語を覚えましょう。',
        hiragana: ['たいせつ','な','ものがたり','を','おぼえましょう'],
        romaji: 'Taisetsu na monogatari o oboemashou.',
        en: "Let's remember this important story.",
      },
    ]
  },
  // Day 29: The future
  {
    day: 29,
    title: "The Future",
    titleJp: "未来",
    newWords: [
      { jp: 'みらい', romaji: 'mirai' },
      { jp: 'ゆめ', romaji: 'yume' },
      { jp: 'きぼう', romaji: 'kibou' },
      { jp: 'ひらいて', romaji: 'hiraite' },
    ],
    lines: [
      {
        id: '29-1',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: '未来は私たちの手の中にあります。',
        hiragana: ['みらい','は','わたしたち','の','て','の','なか','に','あります'],
        romaji: 'Mirai wa watashitachi no te no naka ni arimasu.',
        en: "The future is in our hands.",
      },
      {
        id: '29-2',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___に向かって進みましょう！',
        hiragana: ['ワンワン','みらい','に','むかって','すすみましょう'],
        romaji: 'Wanwan! ___ ni mukatte susumimashou!',
        en: "Woof woof! Let's move toward the future!",
        tiles: [
          { jp: 'みらい', romaji: 'mirai' },
          { jp: 'ゆめ', romaji: 'yume' },
          { jp: 'きぼう', romaji: 'kibou' },
        ],
        answer: { jp: 'みらい', romaji: 'mirai' },
      },
      {
        id: '29-3',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___を追いかけましょう！',
        hiragana: ['きーきー','ゆめ','を','おいかけましょう'],
        romaji: 'Kiiki! ___ o oikakemashou!',
        en: "Kee-kee! Let's chase our dreams!",
        tiles: [
          { jp: 'ゆめ', romaji: 'yume' },
          { jp: 'みらい', romaji: 'mirai' },
          { jp: 'きぼう', romaji: 'kibou' },
        ],
        answer: { jp: 'ゆめ', romaji: 'yume' },
      },
      {
        id: '29-4',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'fill-blank',
        jp: 'ケンケン！___を持ちましょう！',
        hiragana: ['ケンケン','きぼう','を','もちましょう'],
        romaji: 'Kenken! ___ o mochimashou!',
        en: "Ken-ken! Let's have hope!",
        tiles: [
          { jp: 'きぼう', romaji: 'kibou' },
          { jp: 'ゆめ', romaji: 'yume' },
          { jp: 'みらい', romaji: 'mirai' },
        ],
        answer: { jp: 'きぼう', romaji: 'kibou' },
      },
      {
        id: '29-5',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'normal',
        jp: '新しい扉を開きましょう。',
        hiragana: ['あたらしい','とびら','を','ひらきましょう'],
        romaji: 'Atarashii tobira o hirakimashou.',
        en: "Let's open new doors.",
      },
    ]
  },
  // Day 30: The end and beginning
  {
    day: 30,
    title: "The End and Beginning",
    titleJp: "終わりと始まり",
    newWords: [
      { jp: 'おわり', romaji: 'owari' },
      { jp: 'はじまり', romaji: 'hajimari' },
      { jp: 'ありがとう', romaji: 'arigatou' },
      { jp: 'さようなら', romaji: 'sayounara' },
    ],
    lines: [
      {
        id: '30-1',
        speaker: 'narrator',
        avatar: '📖',
        type: 'normal',
        jp: 'これは終わりであり、新しい始まりでもあります。',
        hiragana: ['これ','は','おわり','で','あり','あたらしい','はじまり','でも','あります'],
        romaji: 'Kore wa owari de ari, atarashii hajimari demo arimasu.',
        en: "This is an end, but also a new beginning.",
      },
      {
        id: '30-2',
        speaker: 'momotaro',
        avatar: '👶',
        type: 'fill-blank',
        jp: 'この___をありがとう。',
        hiragana: ['この','たび','を','ありがとう'],
        romaji: 'Kono ___ o arigatou.',
        en: "Thank you for this journey.",
        tiles: [
          { jp: 'たび', romaji: 'tabi' },
          { jp: 'おわり', romaji: 'owari' },
          { jp: 'はじまり', romaji: 'hajimari' },
        ],
        answer: { jp: 'たび', romaji: 'tabi' },
      },
      {
        id: '30-3',
        speaker: 'inu',
        avatar: '🐕',
        type: 'fill-blank',
        jp: 'ワンワン！___！',
        hiragana: ['ワンワン','ありがとう'],
        romaji: 'Wanwan! ___!',
        en: "Woof woof! Thank you!",
        tiles: [
          { jp: 'ありがとう', romaji: 'arigatou' },
          { jp: 'さようなら', romaji: 'sayounara' },
          { jp: 'こんにちは', romaji: 'konnichiwa' },
        ],
        answer: { jp: 'ありがとう', romaji: 'arigatou' },
      },
      {
        id: '30-4',
        speaker: 'saru',
        avatar: '🐒',
        type: 'fill-blank',
        jp: 'きーきー！___！',
        hiragana: ['きーきー','さようなら'],
        romaji: 'Kiiki! ___!',
        en: "Kee-kee! Goodbye!",
        tiles: [
          { jp: 'さようなら', romaji: 'sayounara' },
          { jp: 'ありがとう', romaji: 'arigatou' },
          { jp: 'こんにちは', romaji: 'konnichiwa' },
        ],
        answer: { jp: 'さようなら', romaji: 'sayounara' },
      },
      {
        id: '30-5',
        speaker: 'kiji',
        avatar: '🦃',
        type: 'normal',
        jp: 'ケンケン！また会いましょう！',
        hiragana: ['ケンケン','また','あいましょう'],
        romaji: 'Kenken! Mata aimashou!',
        en: "Ken-ken! Let's meet again!",
      },
    ]
  },
];

// Helper: get today's story segment (up to today)
export function getTodaysStorySegment(): StoryDay[] {
  // Get a story day based on today's date using the daily content system
  const dailyStory = getDailyContent(storyDays);
  
  // Return the story as an array to maintain compatibility
  return [dailyStory];
} 